import type { PlantActivationEffects, PlantActivationEntry } from '../../types/entities';

export interface PlantActivationNotice {
  title?: string;
  message: string;
}

/** Deutsche Aufzaehlung: ", " zwischen den Elementen, " und " vor dem letzten
 * (`a, b und c`; bei zwei Elementen `a und b`; bei einem Element nur `a`). */
function joinGerman(parts: string[]): string {
  if (parts.length <= 1) {
    return parts.join('');
  }
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

function describeRemoved(entry: PlantActivationEntry): string {
  return `${entry.form} ${entry.period}`;
}

function describeCreated(entry: PlantActivationEntry): string {
  return `${entry.period} (${entry.form})`;
}

/**
 * D-16: Rueckmeldung nach dem Speichern einer Werksstilllegung (`notifications.show`, Sketch 027
 * Variante D, Funktion `savePlant`). Formt ausschliesslich die vom Backend in `activationEffects`
 * gelieferten Eintraege zu Titel und Text -- WELCHE Meldungen entfernt (D-08) oder neu angelegt
 * (D-09) wurden, bestimmt ausschliesslich der `PlantActivationProcessor`. Periodenbeschriftungen
 * kommen wie geliefert (z. B. "Q3 2026"), es wird nichts umgeformt oder nachgerechnet.
 */
export function plantActivationNotice(
  effects: PlantActivationEffects | null | undefined
): PlantActivationNotice {
  if (!effects) {
    return { message: 'Werk gespeichert.' };
  }

  if (effects.change === 'reopened') {
    if (effects.created.length === 0) {
      return { title: 'Stilllegung aufgehoben', message: 'Keine neuen Meldungen angelegt.' };
    }
    return {
      title: 'Stilllegung aufgehoben',
      message: `Meldungen für ${joinGerman(effects.created.map(describeCreated))} angelegt.`,
    };
  }

  // 'closed' oder 'rescheduled': D-08, rueckwirkend entfernte Entwuerfe.
  if (effects.removed.length === 0) {
    if (effects.change === 'closed') {
      return { title: 'Werk gespeichert', message: 'Keine Meldungen nach dem Datum.' };
    }
    return { message: 'Werk gespeichert.' };
  }

  const noun = effects.removed.length === 1 ? 'Entwurf' : 'Entwürfe';
  const list = effects.removed.map(describeRemoved).join(', ');
  return {
    title: 'Werk gespeichert',
    message: `${effects.removed.length} ${noun} nach der Stilllegung entfernt: ${list}.`,
  };
}
